import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

export const useAppStore = defineStore('app', () => {
  // State
  const isLoading = ref(false)
  const loadingMessage = ref('Loading...')
  const toasts = ref([])
  const sidebarCollapsed = ref(false)
  const notifications = ref([])
  
  // Auto-incrementing ID for toasts
  let toastId = 0

  // Getters
  const unreadNotifications = computed(() => 
    notifications.value.filter(n => !n.read).length
  )

  // Actions
  const setLoading = (loading, message = 'Loading...') => {
    isLoading.value = loading
    loadingMessage.value = message
  }

  const showToast = (toast) => {
    const id = ++toastId
    const newToast = {
      id,
      type: 'info',
      duration: 5000,
      ...toast
    }
    
    toasts.value.push(newToast)
    
    // Auto-remove toast after duration
    if (newToast.duration > 0) {
      setTimeout(() => {
        removeToast(id)
      }, newToast.duration)
    }
    
    return id
  }

  const removeToast = (id) => {
    const index = toasts.value.findIndex(toast => toast.id === id)
    if (index > -1) {
      toasts.value.splice(index, 1)
    }
  }

  const clearAllToasts = () => {
    toasts.value = []
  }

  // Convenience methods for different toast types
  const showSuccess = (message, options = {}) => {
    return showToast({
      type: 'success',
      title: 'Success',
      message,
      ...options
    })
  }

  const showError = (message, options = {}) => {
    return showToast({
      type: 'error',
      title: 'Error',
      message,
      duration: 7000, // Longer duration for errors
      ...options
    })
  }

  const showWarning = (message, options = {}) => {
    return showToast({
      type: 'warning',
      title: 'Warning',
      message,
      ...options
    })
  }

  const showInfo = (message, options = {}) => {
    return showToast({
      type: 'info',
      title: 'Info',
      message,
      ...options
    })
  }

  const toggleSidebar = () => {
    sidebarCollapsed.value = !sidebarCollapsed.value
    localStorage.setItem('sidebarCollapsed', sidebarCollapsed.value.toString())
  }

  const setSidebarCollapsed = (collapsed) => {
    sidebarCollapsed.value = collapsed
    localStorage.setItem('sidebarCollapsed', collapsed.toString())
  }

  const initSidebar = () => {
    const saved = localStorage.getItem('sidebarCollapsed')
    if (saved !== null) {
      sidebarCollapsed.value = saved === 'true'
    }
  }

  const addNotification = (notification) => {
    const newNotification = {
      id: Date.now(),
      read: false,
      createdAt: new Date(),
      ...notification
    }
    notifications.value.unshift(newNotification)
  }

  const markNotificationAsRead = (id) => {
    const notification = notifications.value.find(n => n.id === id)
    if (notification) {
      notification.read = true
    }
  }

  const markAllNotificationsAsRead = () => {
    notifications.value.forEach(n => n.read = true)
  }

  const removeNotification = (id) => {
    const index = notifications.value.findIndex(n => n.id === id)
    if (index > -1) {
      notifications.value.splice(index, 1)
    }
  }

  return {
    // State
    isLoading,
    loadingMessage,
    toasts,
    sidebarCollapsed,
    notifications,
    
    // Getters
    unreadNotifications,
    
    // Actions
    setLoading,
    showToast,
    removeToast,
    clearAllToasts,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    toggleSidebar,
    setSidebarCollapsed,
    initSidebar,
    addNotification,
    markNotificationAsRead,
    markAllNotificationsAsRead,
    removeNotification
  }
})